import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BlockStack,
  Card,
  FormLayout,
  Layout,
  Page,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";

const STORAGE_KEY = "gamification_scratch_game_defaults_v1";

export type ScratchCatalogTheme = "foil" | "silver" | "gold";

interface StoredScratchDefaults {
  ctaText: string;
  theme: ScratchCatalogTheme;
}

function loadStored(): StoredScratchDefaults {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ctaText: "Scratch to reveal", theme: "foil" };
    }
    const parsed = JSON.parse(raw) as Partial<StoredScratchDefaults>;
    const theme =
      parsed.theme === "silver" || parsed.theme === "gold" || parsed.theme === "foil"
        ? parsed.theme
        : "foil";
    return {
      ctaText: typeof parsed.ctaText === "string" ? parsed.ctaText : "Scratch to reveal",
      theme,
    };
  } catch {
    return { ctaText: "Scratch to reveal", theme: "foil" };
  }
}

export default function ScratchGameConfigPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [ctaText, setCtaText] = useState("");
  const [theme, setTheme] = useState<ScratchCatalogTheme>("foil");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const s = loadStored();
    setCtaText(s.ctaText);
    setTheme(s.theme);
  }, []);

  const backToGames = useCallback(() => {
    navigate({ pathname: "/games", search: location.search });
  }, [navigate, location.search]);

  const handleSave = () => {
    const payload: StoredScratchDefaults = {
      ctaText: ctaText.trim() || "Scratch to reveal",
      theme,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  return (
    <Page
      title="Scratch card"
      subtitle="Default CTA and theme for new scratch experiences (stored in this browser for now)."
      backAction={{ content: "Games", onAction: backToGames }}
      primaryAction={{ content: "Save", onAction: handleSave }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: "1rem" }}>
              <BlockStack gap="400">
                {saved ? (
                  <Text as="p" tone="success">
                    Saved locally.
                  </Text>
                ) : null}
                <FormLayout>
                  <TextField
                    label="CTA text"
                    value={ctaText}
                    onChange={setCtaText}
                    autoComplete="off"
                    helpText="Shown on the scratch surface before reveal."
                  />
                  <Select
                    label="Theme"
                    options={[
                      { label: "Foil", value: "foil" },
                      { label: "Silver", value: "silver" },
                      { label: "Gold", value: "gold" },
                    ]}
                    value={theme}
                    onChange={(v) => setTheme(v as ScratchCatalogTheme)}
                  />
                </FormLayout>
              </BlockStack>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
